import React, { useCallback, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import {
    Box,
    Button,
    CircularProgress,
    Stack,
    Typography,
} from "@mui/material";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../../admin/welcome/downloadPngDataUrl";
import CompanyLogoSvg, { COMPANY_LOGO_SIZE } from "./CompanyLogoSvg";

/** Google AdMob landscape — 1.91:1, must be &gt; 600×314; 1200×628 is the common upload size. */
export const ADMOB_LANDSCAPE_W = 1200;
export const ADMOB_LANDSCAPE_H = 628;
export const ADMOB_LANDSCAPE_FILE = "puzzle-interact-admob-landscape-1200x628.png";

/** Google AdMob square — 1:1, at least 300×300. */
export const ADMOB_SQUARE_W = 1200;
export const ADMOB_SQUARE_H = 1200;
export const ADMOB_SQUARE_FILE = "puzzle-interact-admob-square-1200x1200.png";

/** Soft limit reminder (AdMob upload max 5120 KB). */
export const ADMOB_MAX_FILE_KB = 5120;

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const BRAND_STACK =
    '"Segoe UI", "Helvetica Neue", Arial, "Noto Sans", sans-serif';

function AdMobCreative({ width, height, logoScale, titleSize, subtitleSize, frameRef }) {
    const logoPx = Math.round(COMPANY_LOGO_SIZE * logoScale);
    return (
        <Box
            ref={frameRef}
            sx={{
                width,
                height,
                position: "relative",
                overflow: "hidden",
                background:
                    "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 42%, #CBD5E1 100%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: Math.round(height * 0.035),
                boxSizing: "border-box",
                px: Math.round(width * 0.06),
            }}
        >
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.75) 0%, transparent 70%)",
                    pointerEvents: "none",
                }}
            />
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    gap: Math.round(height * 0.035),
                }}
            >
                <Box
                    sx={{
                        width: logoPx,
                        height: logoPx,
                        flexShrink: 0,
                        borderRadius: Math.round(logoPx * 0.18),
                        overflow: "hidden",
                        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.18)",
                    }}
                >
                    <Box
                        sx={{
                            width: COMPANY_LOGO_SIZE,
                            height: COMPANY_LOGO_SIZE,
                            transform: `scale(${logoScale})`,
                            transformOrigin: "top left",
                            lineHeight: 0,
                        }}
                    >
                        <CompanyLogoSvg />
                    </Box>
                </Box>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <Typography
                        component="div"
                        sx={{
                            fontFamily: BRAND_STACK,
                            fontWeight: 800,
                            fontSize: titleSize,
                            letterSpacing: "-0.03em",
                            color: "#0F172A",
                            lineHeight: 1.15,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                        }}
                    >
                        Puzzle Interact
                    </Typography>
                    <Typography
                        component="div"
                        sx={{
                            mt: 0.75,
                            fontFamily: BRAND_STACK,
                            fontWeight: 600,
                            fontSize: subtitleSize,
                            color: "#475569",
                            letterSpacing: "0.02em",
                            lineHeight: 1.2,
                            whiteSpace: "nowrap",
                            textAlign: "center",
                        }}
                    >
                        Studio of puzzle games
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}

function FormatCard({
    title,
    subtitle,
    width,
    height,
    fileName,
    logoScale,
    titleSize,
    subtitleSize,
    previewMaxW,
    savingKey,
    saving,
    saveError,
    onDownload,
}) {
    const frameRef = useRef(null);
    const previewScale = Math.min(1, previewMaxW / width);
    const previewH = Math.round(height * previewScale);
    const previewW = Math.round(width * previewScale);
    const busy = saving === savingKey;

    return (
        <Box
            sx={{
                flex: "1 1 320px",
                maxWidth: 560,
                bgcolor: "background.paper",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                p: 2,
                boxShadow: "0 4px 20px rgba(15,23,42,0.06)",
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.25 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {subtitle}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    mx: "auto",
                    mb: 1.5,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    boxShadow: "0 6px 24px rgba(15,23,42,0.12)",
                    bgcolor: "#E2E8F0",
                    flexShrink: 0,
                }}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width,
                        height,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                    }}
                >
                    <AdMobCreative
                        width={width}
                        height={height}
                        logoScale={logoScale}
                        titleSize={titleSize}
                        subtitleSize={subtitleSize}
                        frameRef={frameRef}
                    />
                </Box>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        busy ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    disabled={saving != null}
                    onClick={() => onDownload(frameRef, fileName, savingKey)}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#0ea5e9",
                        "&:hover": { bgcolor: "#0284c7" },
                    }}
                >
                    Download PNG
                </Button>
            </Stack>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", textAlign: "center", mt: 1 }}
            >
                {width}×{height} · {fileName} · max {ADMOB_MAX_FILE_KB} KB
            </Typography>
            {saveError && savingKey === saveError.key ? (
                <Typography
                    variant="caption"
                    color="error"
                    sx={{ display: "block", textAlign: "center", mt: 0.5 }}
                >
                    {saveError.message}
                </Typography>
            ) : null}
        </Box>
    );
}

/**
 * Company Google AdMob creatives — landscape (1.91:1) + square (1:1) with download.
 */
export default function CompanyGoogleAdMobPage() {
    const [saving, setSaving] = useState(null);
    const [saveError, setSaveError] = useState(null);

    const handleDownload = useCallback(async (frameRef, fileName, savingKey) => {
        const node = frameRef.current;
        if (!node || saving != null) return;
        setSaving(savingKey);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                ...TO_PNG_OPTS,
                width: node.offsetWidth,
                height: node.offsetHeight,
                backgroundColor: "#F8FAFC",
            });
            await downloadPngDataUrl(dataUrl, fileName);
        } catch (e) {
            setSaveError({
                key: savingKey,
                message: e?.message || "Failed to download PNG",
            });
        } finally {
            setSaving(null);
        }
    }, [saving]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#F1F5F9",
                py: { xs: 2, sm: 3 },
                px: { xs: 1.5, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ maxWidth: 1100, mx: "auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Google AdMob — Company
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                    Upload assets for AdMob image creatives. Landscape must be 1.91:1 and greater than
                    600×314; square must be 1:1 and at least 300×300. File size limit {ADMOB_MAX_FILE_KB}{" "}
                    KB each.
                </Typography>
                <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2.5}
                    useFlexGap
                    flexWrap="wrap"
                    justifyContent="center"
                >
                    <FormatCard
                        title="Landscape image"
                        subtitle="Ratio 1.91:1 · greater than 600×314"
                        width={ADMOB_LANDSCAPE_W}
                        height={ADMOB_LANDSCAPE_H}
                        fileName={ADMOB_LANDSCAPE_FILE}
                        logoScale={0.42}
                        titleSize={52}
                        subtitleSize={22}
                        previewMaxW={480}
                        savingKey="landscape"
                        saving={saving}
                        saveError={saveError}
                        onDownload={handleDownload}
                    />
                    <FormatCard
                        title="Square image"
                        subtitle="Ratio 1:1 · at least 300×300"
                        width={ADMOB_SQUARE_W}
                        height={ADMOB_SQUARE_H}
                        fileName={ADMOB_SQUARE_FILE}
                        logoScale={0.55}
                        titleSize={64}
                        subtitleSize={26}
                        previewMaxW={360}
                        savingKey="square"
                        saving={saving}
                        saveError={saveError}
                        onDownload={handleDownload}
                    />
                </Stack>
            </Box>
        </Box>
    );
}
